import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, RefreshCw, Search } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { th } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface DepletionFiltersProps {
  startDate: Date;
  endDate: Date;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
  onSearch: () => void;
  onRefresh: () => void;
  lastFetchedAt: Date | null;
  isLoading: boolean;
}

export function DepletionFilters({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onSearch,
  onRefresh,
  lastFetchedAt,
  isLoading,
}: DepletionFiltersProps) {
  return (
    <div className="bg-card border rounded-lg p-4 space-y-4">
      <div className="flex flex-wrap items-center gap-4">
        {/* Start Date */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">วันที่เริ่มต้น:</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-[180px] justify-start text-left font-normal",
                  !startDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {startDate ? format(startDate, "dd/MM/yyyy") : "เลือกวันที่"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => date && onStartDateChange(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* End Date */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">วันที่สิ้นสุด:</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-[180px] justify-start text-left font-normal",
                  !endDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {endDate ? format(endDate, "dd/MM/yyyy") : "เลือกวันที่"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={(date) => date && onEndDateChange(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <Button onClick={onSearch} disabled={isLoading}>
            <Search className="mr-2 h-4 w-4" />
            ค้นหา
          </Button>
          <Button variant="outline" onClick={onRefresh} disabled={isLoading}>
            <RefreshCw className={cn("mr-2 h-4 w-4", isLoading && "animate-spin")} />
            รีเฟรช
          </Button>
        </div>
      </div>

      {/* Last Fetched Time */}
      {lastFetchedAt && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>⏱️ ดึงข้อมูลล่าสุด:</span>
          <span className="font-medium">
            {format(lastFetchedAt, "HH:mm:ss")}
          </span>
          <span>
            ({formatDistanceToNow(lastFetchedAt, { addSuffix: true, locale: th })})
          </span>
        </div>
      )}
    </div>
  );
}
